/**
 * Push notification identity (OneSignal).
 *
 * Cocopool's Android app is a WebToNative WebView wrapper. WebToNative bundles the
 * OneSignal SDK into the build, so the device registers itself with OneSignal's servers
 * without this app being involved — the push token never reaches us and we store nothing.
 *
 * The one thing OneSignal cannot know is WHO that device belongs to. That is all this
 * file does: it tells the native SDK the Sharetribe user id (OneSignal calls it the
 * "external id"), so a notification can later be addressed to a person rather than to a
 * device. Sending happens outside this app entirely — see docs/push-notifications.md.
 *
 * Everything here is best-effort and silent: a plain browser, a server render, a missing
 * bridge — all of them mean "no push for this session", never a visible failure.
 */

// WHICH GLOBAL IS THE BRIDGE — this cost a day, so it is written down.
//
// WebToNative's docs talk about `window.WTN`, but the shell does NOT inject that. `WTN`
// is created by WebToNative's own JS library, which you have to load yourself (script tag
// or the `webtonative` npm package). What the Android shell actually injects is an
// @JavascriptInterface named `WebToNativeInterface`, and their library is a thin wrapper
// over it — from webtonative@1.1.10, utills.js:
//
//     var webToNative = (isClient && window.WebToNativeInterface) || {};
//     var platform = webToNative.getAndroidVersion ? "ANDROID_APP" : ...
//
// and OneSignal/index.js calls `webToNative.setExternalUserId(userId)` with a plain
// string. So we call that object directly instead of taking the dependency for two
// methods. `window.WTN` is still preferred when present, in case a page ever loads their
// library — both expose the same two method names.
const wtnLibraryBridge = () => {
  const oneSignal = typeof window !== 'undefined' && window.WTN ? window.WTN.OneSignal : null;
  return oneSignal && typeof oneSignal.setExternalUserId === 'function' ? oneSignal : null;
};

const nativeInterface = () => {
  const iface = typeof window !== 'undefined' ? window.WebToNativeInterface : null;
  return iface && typeof iface.setExternalUserId === 'function' ? iface : null;
};

const bridge = () => wtnLibraryBridge() || nativeInterface();

// The interface is injected by the shell rather than served with the page, so it can show
// up AFTER our code has run. `fetchCurrentUser` resolves almost immediately on a warm
// session, which is early enough to lose that race — and losing it is silent. So a
// missing bridge is treated as "not yet" rather than "never", for a while.
const BRIDGE_WAIT_MS = 10000;
const BRIDGE_POLL_MS = 250;

/**
 * True only inside the WebToNative shell. Absent in a normal browser and during SSR,
 * which is why the `typeof window` guard lives inside the bridge lookups — this runs
 * during server-side `loadData` too.
 */
export const isNativeApp = () =>
  typeof window !== 'undefined' && (!!window.WebToNativeInterface || !!window.WTN);

// Module-scoped, not component state — see identifyUserForPush.
let identifiedUserId = null;
let pendingUserId = null;
let pollTimer = null;

// --- Diagnostics -----------------------------------------------------------------
// TEMPORARY. Opening the app with `?pushdebug=1` reports what the device actually sees.
// It exists because a WebToNative build without remote debugging enabled gives you no
// console at all. Inert without the query parameter. Delete once push is confirmed.

const isDebugMode = () =>
  typeof window !== 'undefined' && /[?&]pushdebug=1/.test(window.location.search || '');

const reportDebug = (outcome, userId) => {
  if (!isDebugMode()) {
    return;
  }
  const iface = typeof window !== 'undefined' ? window.WebToNativeInterface : null;
  const lines = [
    `outcome:   ${outcome}`,
    `userId:    ${userId || '(sin sesión)'}`,
    `url:       ${window.location.href}`,
    `WTN:       ${typeof window.WTN}`,
    `Interface: ${typeof iface}`,
    `métodos:   ${
      iface
        ? Object.keys(iface)
            .slice(0, 12)
            .join(', ')
        : '—'
    }`,
  ];
  // eslint-disable-next-line no-alert
  window.alert(`[push debug]\n\n${lines.join('\n')}`);
};

// ---------------------------------------------------------------------------------

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

// Hands `pendingUserId` to the bridge. Returns true once that has happened.
const tryIdentify = () => {
  const target = bridge();
  if (!target || !pendingUserId) {
    return false;
  }
  try {
    target.setExternalUserId(pendingUserId);
    identifiedUserId = pendingUserId;
    reportDebug('identificado', pendingUserId);
    pendingUserId = null;
    return true;
  } catch (e) {
    // A bridge that changed shape must not take the session down with it. Leaving
    // identifiedUserId unset means the next page load is free to try again.
    reportDebug(`error: ${e.message}`, pendingUserId);
    pendingUserId = null;
    return true;
  }
};

/**
 * Tell OneSignal which Cocopool user this device belongs to.
 *
 * Called from the success path of `fetchCurrentUser`, which fires on every page load and
 * every login, so the guard has to be real. It lives at module scope because the obvious
 * alternative — a `useRef` in a component — resets whenever that component remounts, and
 * the natural mount point (TopbarContainer) remounts on every navigation.
 *
 * Re-identifying is what makes a shared device behave: when a different person signs in,
 * OneSignal re-attributes the subscription to them, so a notification for the previous
 * owner stops landing on that phone.
 *
 * @param {string} userId Sharetribe user uuid
 */
export const identifyUserForPush = userId => {
  if (typeof window === 'undefined' || !userId || identifiedUserId === userId) {
    return;
  }

  pendingUserId = userId;

  // Fast path: the bridge is already there, so this stays synchronous.
  if (tryIdentify()) {
    stopPolling();
    return;
  }

  // Otherwise wait for the shell to inject it. In a plain browser this simply expires —
  // 40 property checks over 10s, which costs nothing and is the price of not having a
  // reliable way to tell a slow WebView from a desktop tab.
  if (pollTimer) {
    return;
  }
  const startedAt = Date.now();
  pollTimer = setInterval(() => {
    if (tryIdentify() || Date.now() - startedAt >= BRIDGE_WAIT_MS) {
      const timedOut = pendingUserId !== null;
      stopPolling();
      if (timedOut) {
        reportDebug('sin puente tras esperar', pendingUserId);
        pendingUserId = null;
      }
    }
  }, BRIDGE_POLL_MS);
};

/**
 * Detach this device from the user, on logout.
 *
 * Synchronous and never throws: the logout thunk calls it inline, and nothing about
 * signing out may depend on the native bridge answering.
 */
export const clearPushIdentity = () => {
  identifiedUserId = null;
  pendingUserId = null;
  stopPolling();

  const target = bridge();
  if (!target || typeof target.removeExternalUserId !== 'function') {
    return;
  }

  try {
    target.removeExternalUserId();
  } catch (e) {
    // Nothing to do — the next sign-in re-attributes the device anyway.
  }
};

// Test seam: the module-level dedupe would otherwise leak between cases.
export const resetPushIdentityForTests = () => {
  identifiedUserId = null;
  pendingUserId = null;
  stopPolling();
};
